import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

/**
 * SUBMIT schema — the AutoForm contract for `PUT /genres/{id}`.
 *
 * **`enrichmentStatus` is deliberately NOT here.** It used to be, as a plain
 * `z.enum(...)` AutoForm field, which rendered a dropdown letting a curator set
 * a genre to `Enriched` with no enrichment having run — a lifecycle value only
 * the enricher may write. It lives on the read schema below, where the preview
 * renders it, and the edit form can no longer submit it.
 *
 * **`.passthrough()` is load-bearing, not tidiness.** AutoForm validates the
 * whole loaded document with this schema before `edit.tsx`'s `onSubmit` sees
 * it, so a bare `z.object()` drops every key IMS does not model. Genre is the
 * odd one of the nine enriched entities: its per-locale `translations` map sits
 * **flat** on the document, not under `enrichment`, and genre has no
 * enrichment-review page — this is the only schema between the map and a
 * curator save. The map is written by Tina's translate stage, a curator never
 * edits it, and there is no field for it here on purpose. Enumerating it
 * instead would just re-break on the next key the enrichment flow adds, which
 * is the lesson `artist.aienrichment.form.ts`'s submit schema and
 * `event.edit.form.ts`'s `metadata` already paid for.
 */
export const genresEditSchema = z
  .object({
    id: z.string().readonly(),
    name: z.string(),
    about: z
      .string()
      .nullish()
      .superRefine(
        fieldConfig({
          fieldType: "textarea",
        }),
      ),
    aliases: z.array(z.string()).nullish(),
    parents: z.array(z.string()).nullish(),
    features: z
      .object({
        commerciality: z.number().nullish(),
        danceability: z.number().nullish(),
        energy: z.number().nullish(),
        grit: z.number().nullish(),
        organicness: z.number().nullish(),
        tempo: z.number().nullish(),
        valence: z.number().nullish(),
        vocality: z.number().nullish(),
      })
      .nullish(),
    metadata: z
      .object({
        source: z.string(),
        provider: z.string(),
        urls: z.array(z.string()).nullish(),
      })
      .nullish(),
    transliterations: z
      .object({
        en: z.array(z.string()).nullish(),
        el: z.array(z.string()),
        ar: z.array(z.string()),
        ro: z.array(z.string()),
      })
      .nullish(),
    deprecated: z.boolean().default(false),
  })
  .passthrough();

/**
 * READ schema — lenient, and the only place the enrichment lifecycle fields
 * appear.
 *
 * `genres/edit.tsx` used to hard-`.parse()` the response with the edit schema,
 * so one off-contract field blanked the page; and because the edit schema was
 * also the read schema, `embeddingStatus` and `enrichmentRetryCount` were
 * simply invisible — genre is one of the nine enriched and four embeddable
 * types. All three are `z.string()`/`z.number()` rather than enums so a value
 * the backend grows before IMS redeploys still renders.
 */
export const genresReadSchema = genresEditSchema
  .partial()
  .extend({
    id: z.string(),
    name: z.string(),
    enrichmentStatus: z.string().nullish(),
    embeddingStatus: z.string().nullish(),
    enrichmentRetryCount: z.coerce.number().nullish(),
  })
  .passthrough();

export type GenresEditFormType = z.infer<typeof genresEditSchema>;
export type GenresReadType = z.infer<typeof genresReadSchema>;
